import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { calculateAccommodationNights } from "@/lib/itineraryCostUtils";

export interface AccommodationOverrideItem {
  id: string;
  title?: string | null;
  supplier?: string | null;
  occupancy_type?: string | null;
  cost?: number | null;
  quantity?: number | null;
  check_in?: string | null;
  check_out?: string | null;
}

interface Props {
  items: AccommodationOverrideItem[];
  overrides: Record<string, number>;
  onChange: (itemId: string, rooms: number) => void;
  onReset: () => void;
  currencySymbol: string;
}

export const AccommodationRoomOverridePanel = ({
  items,
  overrides,
  onChange,
  onReset,
  currencySymbol,
}: Props) => {
  if (!items.length) return null;

  const hasOverrides = Object.keys(overrides).length > 0;

  return (
    <div className="space-y-2 pt-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">
          Adjust rooms per accommodation (snapshot only — itinerary unchanged)
        </p>
        {hasOverrides && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={onReset}
          >
            Reset to original
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {items.map((item) => {
          const nights = calculateAccommodationNights(item.check_in, item.check_out);
          const originalRooms = item.quantity || 1;
          const rooms = overrides[item.id] ?? originalRooms;
          const cost = item.cost || 0;
          const lineTotal = cost * Math.max(nights, 1) * rooms;
          const isOverridden = overrides[item.id] !== undefined && overrides[item.id] !== originalRooms;

          return (
            <div
              key={item.id}
              className="flex items-center gap-2 text-xs p-2 rounded bg-background border"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {item.title || item.supplier || "Accommodation"}
                  {item.occupancy_type && (
                    <span className="text-muted-foreground font-normal"> ({item.occupancy_type})</span>
                  )}
                </div>
                <div className="text-muted-foreground">
                  {nights} {nights === 1 ? "night" : "nights"} × {currencySymbol}
                  {cost.toFixed(2)}/room
                </div>
              </div>
              <Input
                type="number"
                min={1}
                value={rooms}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v) && v >= 1) onChange(item.id, v);
                }}
                className="h-8 w-16 text-center"
              />
              <div className={`w-24 text-right font-semibold ${isOverridden ? "text-primary" : ""}`}>
                {currencySymbol}
                {lineTotal.toFixed(2)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};